<script>
import axios from 'axios';

export default {
  name: 'AdicionarContatoRapidoModal',
  props: {
    visivel: { type: Boolean, default: false },
    nomeSugerido: { type: String, default: '' },
    telefoneSugerido: { type: String, default: '' },
    // Identificadores WhatsApp para o caso sem telefone (username/LID).
    whatsappUsername: { type: String, default: '' },
    jid: { type: String, default: '' },
  },
  emits: ['fechar', 'salvo'],
  data() {
    return {
      nome: '',
      sobrenome: '',
      telefone: '',
      status_contato: 'ativo',
      salvando: false,
      erro: null,
    };
  },
  computed: {
    // Sem telefone, o participante veio anônimo (LID/username) — cadastramos
    // pelo identificador WhatsApp disponível.
    semTelefone() {
      return !String(this.telefoneSugerido || '').replace(/\D/g, '');
    },
    podeSalvar() {
      if (this.salvando) return false;
      if (!String(this.nome || '').trim()) return false;
      if (!this.semTelefone && !String(this.telefone || '').replace(/\D/g, '')) return false;
      return true;
    },
  },
  watch: {
    visivel(v) {
      if (v) this._hidratar();
    },
  },
  mounted() {
    if (this.visivel) this._hidratar();
  },
  methods: {
    _hidratar() {
      this.nome = this.nomeSugerido || '';
      this.sobrenome = '';
      this.telefone = this.telefoneSugerido || '';
      this.status_contato = 'ativo';
      this.erro = null;
    },
    fechar() {
      if (this.salvando) return;
      this.$emit('fechar');
    },
    async salvar() {
      if (!this.podeSalvar) return;
      this.salvando = true;
      this.erro = null;
      try {
        const tel = String(this.telefone || '').replace(/\D/g, '');
        // Sempre há um canal telefone (o backend exige ≥1). Sem número, usamos o
        // identificador do WhatsApp (username/jid) como valor do canal.
        const valorCanal = tel ? this.telefone : (this.whatsappUsername || this.jid);
        const canais = [{ tipo_canal: 'telefone', valor_canal: valorCanal, principal: true }];
        const payload = {
          nome: this.nome.trim(),
          sobrenome: (this.sobrenome || '').trim() || undefined,
          status_contato: this.status_contato,
          canais,
        };
        // Repassa identificadores WhatsApp para o backend gravar no canal.
        if (this.whatsappUsername) payload.whatsapp_username = this.whatsappUsername;
        if (this.jid) payload.whatsapp_jid = this.jid;

        const resp = await axios.post('/contato', payload);
        const contato = resp.data?.data || resp.data;
        const jaExistia = Boolean(resp.data?.existente);
        this.$emit('salvo', { contato, jaExistia });
        this.$emit('fechar');
      } catch (error) {
        this.erro = error.response?.data?.error || error.response?.data?.details || error.message;
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <div v-if="visivel" class="modal-backdrop-custom" @click.self="fechar">
      <div class="modal-card shadow-lg">
        <header class="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
          <h6 class="m-0">
            <i class="ri-user-add-line me-1"></i>Adicionar contato
          </h6>
          <button type="button" class="btn btn-link p-0 text-body" :disabled="salvando" @click="fechar">
            <i class="ri-close-line" style="font-size: 1.4rem;"></i>
          </button>
        </header>

        <div class="modal-body-custom p-3">
          <div v-if="semTelefone" class="alert alert-warning py-2 small">
            <i class="ri-shield-user-line me-1"></i>
            Este participante não expõe o telefone (privacidade/username do WhatsApp).
            O contato será cadastrado pelo identificador do WhatsApp.
          </div>

          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label small">Nome *</label>
              <input v-model="nome" type="text" class="form-control form-control-sm" maxlength="200" @keyup.enter="salvar" />
            </div>
            <div class="col-md-6">
              <label class="form-label small">Sobrenome</label>
              <input v-model="sobrenome" type="text" class="form-control form-control-sm" maxlength="200" @keyup.enter="salvar" />
            </div>
            <div v-if="!semTelefone" class="col-md-8">
              <label class="form-label small">Telefone</label>
              <input v-model="telefone" type="text" class="form-control form-control-sm" placeholder="5531999999999" @keyup.enter="salvar" />
              <small class="text-muted">Com DDI/DDD. Puxado do WhatsApp; edite se necessário.</small>
            </div>
            <div class="col-md-4">
              <label class="form-label small">Status</label>
              <select v-model="status_contato" class="form-select form-select-sm">
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
                <option value="bloqueado">Bloqueado</option>
              </select>
            </div>
          </div>

          <div v-if="erro" class="alert alert-danger mt-2 mb-0 py-2 small">
            <i class="ri-error-warning-line me-1"></i>{{ erro }}
          </div>
        </div>

        <footer class="d-flex justify-content-end gap-2 px-3 py-2 border-top">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="salvando" @click="fechar">Cancelar</button>
          <button type="button" class="btn btn-success btn-sm" :disabled="!podeSalvar" @click="salvar">
            <i v-if="salvando" class="ri-loader-4-line spin me-1"></i>
            <i v-else class="ri-check-line me-1"></i>
            {{ salvando ? 'Salvando...' : 'Salvar contato' }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-card {
  background: var(--vz-body-bg);
  color: var(--vz-body-color);
  border-radius: 0.5rem;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}
.modal-body-custom {
  overflow-y: auto;
  flex: 1;
}
.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
